import { DI } from 'aurelia';
import { inject } from "aurelia";
import { ModelTypes, order_by } from '@zeus';
import { IAuthService } from '@interfaces/auth-service/auth-service-interface';
import { IZeusClient, ZeusClient } from '@services/graphql-service/zeus-client';

export type SizeObject = {
  id: string;
  alias: string;
  height: string;
  width: string;
};

export interface ICreateProjectService {
  getFormats(): Promise<SizeObject[]>;
  getBrands(): Promise<ModelTypes["brands"][]>;
  getMedia(): Promise<ModelTypes["userMedia"][]>;
  getProducts(): Promise<ModelTypes["products"][]>;
  getAudiences(): Promise<ModelTypes["audiences"][]>;
  getProductsById(productId: string): Promise<ModelTypes["products"]>;
  getTemplates(command: { height: string, width: string }): Promise<ModelTypes["pTemplate"][]>;
  createNewProject(command: { brandId: string, productId: string, audienceId?: string, templateId: string, image: string, artDirectional: string }): Promise<ModelTypes["CreateAutomaticProjectOutput"]>;
  insertUserMedia(selectedImage: string): Promise<void>;
}

@inject(IAuthService, IZeusClient)
class CreateProjectService implements ICreateProjectService {

  constructor(
    private auth: IAuthService,
    private zeusClient: ZeusClient
  ) { }

  async createNewProject(command: { brandId: string; productId: string; audienceId: string; templateId: string; image: string; artDirectional: string; }): Promise<ModelTypes["CreateAutomaticProjectOutput"]> {
    const result = await this.zeusClient.chain('mutation')({
      createAutomaticProject: [
        {
          brandId: command.brandId,
          productId: command.productId,
          // audienceId: command.audienceId,
          selectedTemplateId: command.templateId,
          selectedImage: command.image,
          artDirectional: command.artDirectional,
        },
        {
          projectId: true,
        }
      ]
    });
    return result.createAutomaticProject
  }

  async getBrands(): Promise<ModelTypes["brands"][]> {
    const result = await this.zeusClient.chain('query')({
      brands: [
        {
          limit: 1,
        },
        {
          id: true,
          brandName: true,
          slogan: true,
          voiceTone: true,
          segment: true,
          productOrService: true,
          fontPrimary: true,
          fontSecondary: true,
          isBrand: true,
          personality: true,
          logoFileId: true,
        }
      ]
    });

    return result.brands as ModelTypes["brands"][];
  }

  async getAudiences(): Promise<ModelTypes["audiences"][]> {
    const result = await this.zeusClient.chain('query')({
      audiences: [
        {},
        {
          id: true,
          brandName: true,
          segment: true,
          productOrService: true,
          targetAudience: true,
          personas: [{ path: '$', }, true],
          created_by: true,
          created_at: true,
          updated_at: true,
        }
      ]
    });

    return result.audiences as ModelTypes["audiences"][];
  }

  async getProducts(): Promise<ModelTypes["products"][]> {
    const result = await this.zeusClient.chain('query')({
      products: [
        {
          where: { created_by: { _eq: this.auth.user.id } },
        },
        {
          id: true,
          alias: true,
          type: true,
          category: true,
          price: true,
          salePrice: true,
          linkConversion: true,
          created_by: true,
          created_at: true,
          updated_at: true,
        }
      ]
    })

    return result.products as ModelTypes["products"][];
  }

  async getProductsById(productId: string): Promise<ModelTypes["products"]> {
    const result = await this.zeusClient.chain('query')({
      products_by_pk: [
        {
          id: productId
        },
        {
          id: true,
          imageOneId: true,
          imageTwoId: true,
          imageThreeId: true,
          imageFourId: true,
        }
      ]
    })

    return result.products_by_pk as ModelTypes["products"];
  }

  async getTemplates(command: { height: string, width: string }): Promise<ModelTypes["pTemplate"][]> {
    const result = await this.zeusClient.chain('query')({
      pTemplate: [
        {
          where: {
            height: { _eq: command.height },
            width: { _eq: command.width },
            deletedAt: { _is_null: true },
          },
          order_by: [{ created_at: order_by.desc }]
        },
        {
          id: true,
          previewFileId: true,
        }
      ]
    });



    return result.pTemplate as ModelTypes["pTemplate"][];
  }

  async getFormats(): Promise<SizeObject[]> {
    const valuesArray: SizeObject[] = [
      { alias: 'Facebook - Feed (1.080 x 1.350)', id: '10801350ff', height: '1350', width: '1080' },
      { alias: 'Facebook - Stories (1.080 x 1.920)', id: '10801920fs', height: '1920', width: '1080' },
      { alias: 'Facebook - Capa da página (851 x 315)', id: '851315fsp', height: '315', width: '851' },
      { alias: 'Facebook - Capa para eventos (1920 x 1.005)', id: '19201005fce', height: '1005', width: '1920' },
      { alias: 'Facebook - Marketplace, Coluna (1.200 x 1.200)', id: '12001200fmc', height: '1200', width: '1200' },
      { alias: 'Facebook - Carrossel, Ads (1.080 x 1.080)', id: '10801080fca', height: '1080', width: '1080' },

      { alias: 'Instagram - Feed ou Carrossel Vertical (1.080 x 1.350)', id: '10801350ifcv', height: '1350', width: '1080' },
      { alias: 'Instagram - Feed ou Carrossel Quadrado (1.080 x 1.080)', id: '10801080ifcq', height: '1080', width: '1080' },
      { alias: 'Instagram - Feed Horizontal (1080 x 566)', id: '1080566ifh', height: '566', width: '1080' },
      { alias: 'Instagram - Stories (1.080 x 1.920)', id: '10801920is', height: '1920', width: '1080' },
      { alias: 'Instagram - Carrossel (1.080 x 1.350)', id: '10801350ic', height: '1350', width: '1080' },

      { alias: 'LinkedIn - Capa pessoal (1.584 x 396)', id: '1584396lcp', height: '396', width: '1584' },
      { alias: 'LinkedIn - Capa de página (1.128 x 191)', id: '1128191lcp', height: '191', width: '1128' },
      { alias: 'LinkedIn - Feed (1.080 x 1.080)', id: '10801080lf', height: '1080', width: '1080' },
    ];

    return valuesArray;
  }

  async getMedia(): Promise<ModelTypes["userMedia"][]> {
    const result = await this.zeusClient.chain('query')({
      userMedia: [
        {
          where: { deleted: { _eq: false } },
          order_by: [
            { created_at: order_by.desc }
          ]
        },
        {
          id: true,
          fileId: true,
          mediaType: true,
        }
      ]
    });

    return result.userMedia as ModelTypes["userMedia"][];
  }

  async insertUserMedia(selectedImage: string): Promise<void> {
    const userId = this.auth.user.id;

    let fileId: string;

    if (selectedImage && selectedImage.startsWith('http')) {
      // fileId = selectedImage.split('/').pop();

      return;
    } else if (selectedImage) {
      fileId = selectedImage;
    }

    if (!fileId) return;

    const userMediaExisy = await this.zeusClient.chain('query')({
      userMedia: [
        {
          where: { fileId: { _eq: fileId } }
        },
        {
          id: true
        }
      ]
    });

    if (userMediaExisy?.userMedia?.length > 0) return;

    await this.zeusClient.chain('mutation')({
      insert_userMedia_one: [
        {
          object: {
            fileId: fileId,
          }
        },
        {
          id: true,
        }
      ],

      updateFile: [
        {
          pk_columns: { id: fileId },
          _set: { uploadedByUserId: userId }
        },
        {
          id: true
        }
      ]
    });
  }

}

export const ICreateProjectService = DI.createInterface<ICreateProjectService>((x) => x.singleton(CreateProjectService));
